'use client';

import { isOurReleaseNoteImageSrc } from '@/@studio-ai/release-notes';
import { withoutTaggedNextReplies } from '@/lib/feasibility/dani/primer-marks';
import { CodeProps, PreProps } from '@/@studio-ai/types';
import dynamic from 'next/dynamic';
import { memo, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';

/**
 * A feasibility plan DANI wrote (`feasibility-plan` block, draft Amendment 11),
 * shown as a card that opens it in Feasibility. Loaded only when a message has one.
 */
const FeasibilityPlanCard = dynamic(
    () =>
        import('@/@feasibility-dani/components/strategy/FeasibilityPlanCard').then(
            (m) => m.FeasibilityPlanCard,
        ),
    { ssr: false },
);

/**
 * A feasibility check DANI asked to run in the chat (`feasibility-check`
 * block): Studio's engine runs it and the card shows the answer, then tells
 * DANI so DANI can explain it. Loaded only when a message has one.
 */
const FeasibilityCheckCard = dynamic(
    () =>
        import('@/@feasibility-dani/components/chat/FeasibilityCheckCard').then(
            (m) => m.FeasibilityCheckCard,
        ),
    { ssr: false },
);

/** A check against a past survey, and past surveys like a check (`feasibility-compare`, `feasibility-similar`). */
const FeasibilityCompareCard = dynamic(
    () =>
        import('@/@feasibility-dani/components/chat/FeasibilityCompareCard').then(
            (m) => m.FeasibilityCompareCard,
        ),
    { ssr: false },
);
/** A longer look at a finished check (`feasibility-investigate`), for any staff member. */
const FeasibilityInvestigateCard = dynamic(
    () =>
        import('@/@feasibility-dani/components/chat/FeasibilityInvestigateCard').then(
            (m) => m.FeasibilityInvestigateCard,
        ),
    { ssr: false },
);
const FeasibilitySimilarCard = dynamic(
    () =>
        import('@/@feasibility-dani/components/chat/FeasibilitySimilarCard').then(
            (m) => m.FeasibilitySimilarCard,
        ),
    { ssr: false },
);
/** The people at a saved list's places, pulled in the chat (`feasibility-visits`, M4S-5904). */
const FeasibilityVisitsCard = dynamic(
    () =>
        import('@/@feasibility-dani/components/chat/FeasibilityVisitsCard').then(
            (m) => m.FeasibilityVisitsCard,
        ),
    { ssr: false },
);

/** A behavioral report DANI offers in place of, or beside, a survey (`feasibility-report`). */
const FeasibilityReportCard = dynamic(
    () =>
        import('@/@feasibility-dani/components/chat/FeasibilityReportCard').then(
            (m) => m.FeasibilityReportCard,
        ),
    { ssr: false },
);

/** DANI handing a conversation to Feasibility (`feasibility-start`). */
const HandoffCard = dynamic(() => import('./chat/HandoffCard').then((m) => m.HandoffCard), {
    ssr: false,
});

/** A survey DANI built in the chat (`survey-draft`, docs/pdlc/builder.md, Build it with DANI). */
const SurveyDraftCard = dynamic(
    () => import('@/@builder/components/chat/SurveyDraftCard').then((m) => m.SurveyDraftCard),
    { ssr: false },
);

/** A saved survey's next step, in the chat (`survey-next`, docs/pdlc/builder.md). */
const SurveyNextCard = dynamic(
    () => import('@/@builder/components/chat/SurveyNextCard').then((m) => m.SurveyNextCard),
    { ssr: false },
);

/** How a launched survey is fielding, in any chat (`survey-status`, docs/pdlc/fielding-status.md). */
const SurveyStatusCard = dynamic(
    () => import('@/@field-status/SurveyStatusCard').then((m) => m.SurveyStatusCard),
    { ssr: false },
);

/**
 * A crosstab plan DANI wrote (`crosstab-plan` block, M4S-5777 X149), shown as a card that opens it
 * in Crosstabs (or, inside a crosstab's Ask DANI panel, applies it there). Loaded only when a
 * message has one.
 */
const CrosstabPlanCard = dynamic(
    () =>
        import('@/app/(private)/ai/components/Crosstabs/dani/CrosstabPlanCard').then(
            (m) => m.CrosstabPlanCard,
        ),
    { ssr: false },
);

/**
 * Builds the react-markdown component overrides. `bodyTextClassName`
 * controls the size of body text (p/li/ul/ol) — callers that need a
 * different size than the default `text-medium` (e.g. the chat message
 * bubbles in text-display.tsx) pass their own class instead of relying on
 * a descendant selector, since Tailwind's JIT doesn't reliably pick up
 * arbitrary-variant + arbitrary-value combos like `[&_p]:text-[16px]`.
 */
function ReleaseNoteMarkdownImage({ src, alt }: { src?: string; alt?: string }) {
    const [broken, setBroken] = useState(false);
    if (!src || broken || !isOurReleaseNoteImageSrc(src)) {
        return <span>{alt || 'Image'}</span>;
    }
    return (
        // Presigned S3 URLs are not in the Next image allow-list, and they expire.
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={src}
            alt={alt || ''}
            className="my-2 h-auto max-w-full"
            onError={() => setBroken(true)}
        />
    );
}

const releaseNoteMarkdownImage: Components['img'] = ({ src, alt }) => (
    <ReleaseNoteMarkdownImage src={typeof src === 'string' ? src : undefined} alt={alt} />
);

export function buildMarkdownComponents(
    bodyTextClassName = 'text-medium',
    imagePolicy: 'default' | 'release-notes' = 'default',
): Partial<Components> {
    return {
        table: ({ ...props }) => (
            <div className="mt-1 max-h-[300px] max-w-full overflow-x-scroll overflow-y-scroll">
                <table
                    {...props}
                    className="w-full border-collapse border border-default-300 text-medium"
                />
            </div>
        ),
        th: ({ ...props }) => (
            <th
                {...props}
                className="border border-default-300 bg-default-200 px-2 py-1 text-left text-medium font-medium leading-tight text-default-foreground"
            />
        ),
        td: ({ ...props }) => (
            <td
                {...props}
                className="border border-default-300 px-2 py-1 text-medium leading-tight"
            />
        ),
        pre: ({ children }: PreProps) => <>{children}</>,
        code: ({ children, className }: CodeProps) => {
            const match = /language-([\w-]+)/.exec(className || '');
            const language = match ? match[1] : '';
            const content = String(children).replace(/\n$/, '');

            if (language === 'feasibility-plan') {
                return <FeasibilityPlanCard content={content} />;
            }
            if (language === 'feasibility-check') {
                return <FeasibilityCheckCard content={content} />;
            }
            if (language === 'feasibility-compare') {
                return <FeasibilityCompareCard content={content} />;
            }
            if (language === 'feasibility-similar') {
                return <FeasibilitySimilarCard content={content} />;
            }
            if (language === 'feasibility-investigate') {
                return <FeasibilityInvestigateCard content={content} />;
            }
            // DANI's suggested next replies: the composer and card show them, not the message.
            if (language === 'feasibility-next') return null;
            if (language === 'crosstab-plan') {
                return <CrosstabPlanCard content={content} />;
            }
            if (language === 'feasibility-report') {
                return <FeasibilityReportCard content={content} />;
            }
            if (language === 'feasibility-visits') {
                return <FeasibilityVisitsCard content={content} />;
            }
            if (language === 'feasibility-start') {
                return <HandoffCard kind={language} content={content} />;
            }
            // DANI hands a survey task to Claude (DANI and Claude side by side).
            if (language === 'survey-start' || language === 'survey-change') {
                return <SurveyDraftCard task={language} content={content} />;
            }
            if (language === 'survey-draft') {
                return <SurveyDraftCard content={content} />;
            }
            // The next step for a saved survey: check, preview, feasibility, publish, launch.
            if (language === 'survey-next') {
                return <SurveyNextCard content={content} />;
            }
            // How a launched survey is fielding, asked for any time after launch.
            if (language === 'survey-status') {
                return <SurveyStatusCard content={content} />;
            }

            if (language) {
                return (
                    <SyntaxHighlighter
                        style={oneDark}
                        language={language}
                        PreTag="div"
                        className="my-2 rounded-md"
                        customStyle={{
                            margin: 0,
                            fontSize: '0.875rem',
                        }}
                    >
                        {content}
                    </SyntaxHighlighter>
                );
            }

            if (content.includes('\n')) {
                return (
                    <pre className="my-2 overflow-x-auto whitespace-pre rounded-md bg-default-100 p-3 font-mono text-medium">
                        {content}
                    </pre>
                );
            }

            return (
                <code className="rounded bg-default-200 px-1 py-0.5 font-mono text-medium">
                    {children}
                </code>
            );
        },
        ol: ({ children, ...props }: React.ComponentProps<'ol'>) => {
            return (
                <ol {...props} className={`ml-4 list-outside list-decimal ${bodyTextClassName}`}>
                    {children}
                </ol>
            );
        },
        li: ({ children, ...props }: React.ComponentProps<'li'>) => {
            return (
                <li {...props} className={`py-0.5 leading-relaxed ${bodyTextClassName}`}>
                    {children}
                </li>
            );
        },
        ul: ({ children, ...props }: React.ComponentProps<'ul'>) => {
            return (
                <ul {...props} className={`ml-4 list-outside list-disc ${bodyTextClassName}`}>
                    {children}
                </ul>
            );
        },
        strong: ({ children, ...props }: React.ComponentProps<'strong'>) => {
            return (
                <strong {...props} className="font-bold">
                    {children}
                </strong>
            );
        },
        a: ({ children, href, ...props }: React.ComponentProps<'a'>) => {
            return (
                <a
                    {...props}
                    href={href}
                    className="text-primary underline hover:text-primary-600"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {children}
                </a>
            );
        },
        h1: ({ children, ...props }: React.ComponentProps<'h1'>) => {
            return (
                <h1 className="mb-2 mt-3 text-large font-semibold" {...props}>
                    {children}
                </h1>
            );
        },
        h2: ({ children, ...props }: React.ComponentProps<'h2'>) => {
            return (
                <h2 className="mb-2 mt-3 text-base font-semibold" {...props}>
                    {children}
                </h2>
            );
        },
        h3: ({ children, ...props }: React.ComponentProps<'h3'>) => {
            return (
                <h3 className="mb-2 mt-3 text-medium font-semibold" {...props}>
                    {children}
                </h3>
            );
        },
        h4: ({ children, ...props }: React.ComponentProps<'h4'>) => {
            return (
                <h4 className="mb-1 mt-2 text-medium font-medium" {...props}>
                    {children}
                </h4>
            );
        },
        h5: ({ children, ...props }: React.ComponentProps<'h5'>) => {
            return (
                <h5
                    className="mb-1 mt-2 text-small font-semibold uppercase tracking-wide"
                    {...props}
                >
                    {children}
                </h5>
            );
        },
        h6: ({ children, ...props }: React.ComponentProps<'h6'>) => {
            return (
                <h6
                    className="mb-1 mt-2 text-small font-medium uppercase tracking-wide text-foreground-500"
                    {...props}
                >
                    {children}
                </h6>
            );
        },
        p: ({ children }: React.ComponentProps<'p'>) => (
            <p className={`mb-1 last:mb-0 ${bodyTextClassName}`}>{children}</p>
        ),
        blockquote: ({ children }: React.ComponentProps<'blockquote'>) => (
            <blockquote className="my-2 border-l-4 border-default-300 pl-4 italic">
                {children}
            </blockquote>
        ),
        ...(imagePolicy === 'release-notes' ? { img: releaseNoteMarkdownImage } : {}),
    };
}

export const markdownComponents: Partial<Components> = buildMarkdownComponents();

export const remarkPlugins = [remarkGfm];

const releaseNoteMarkdownComponents = buildMarkdownComponents('text-medium', 'release-notes');

const NonMemoizedMarkdown = ({
    children,
    components,
    imagePolicy = 'default',
}: {
    children: string;
    components?: Partial<Components>;
    imagePolicy?: 'default' | 'release-notes';
}) => {
    const resolved =
        components ??
        (imagePolicy === 'release-notes' ? releaseNoteMarkdownComponents : markdownComponents);
    return (
        <ReactMarkdown remarkPlugins={remarkPlugins} components={resolved}>
            {withoutTaggedNextReplies(children)}
        </ReactMarkdown>
    );
};

export const Markdown = memo(
    NonMemoizedMarkdown,
    (prevProps, nextProps) =>
        prevProps.children === nextProps.children &&
        prevProps.components === nextProps.components &&
        prevProps.imagePolicy === nextProps.imagePolicy,
);
